import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Star, Image, MessageSquare, Clock, CheckCircle2 } from "lucide-react";
import { Switch } from "./ui/switch";
import { whatsappLink } from "../lib/api";

const checks = [
  { key: "verified", icon: CheckCircle2, label: "Listing is claimed & verified", weight: 20 },
  { key: "photos", icon: Image, label: "10+ recent, high-quality photos", weight: 20 },
  { key: "reviews", icon: Star, label: "You reply to every review", weight: 25 },
  { key: "posts", icon: MessageSquare, label: "You post updates weekly", weight: 20 },
  { key: "hours", icon: Clock, label: "Hours, services & description complete", weight: 15 },
];

export default function GmbAudit() {
  const [state, setState] = useState({ verified: false, photos: false, reviews: false, posts: false, hours: false });

  const score = useMemo(
    () => checks.reduce((acc, c) => acc + (state[c.key] ? c.weight : 0), 0),
    [state]
  );

  const verdict =
    score >= 80 ? { t: "Healthy", c: "text-lab-deep" } :
    score >= 50 ? { t: "Needs care", c: "text-blue-deep" } :
    { t: "Critical", c: "text-rose-500" };

  const circumference = 2 * Math.PI * 52;

  const claim = () => {
    const msg = `Hi Content Lab! I'd like a full Google My Business audit. My self-check score was ${score}/100 (${verdict.t}). Please tell me what's wrong and how to fix it.`;
    window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <section id="gmb" className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-14 flex items-center gap-3">
          <MapPin className="h-5 w-5 text-lab-green" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-blue-deep">/ Google My Business Audit</span>
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="max-w-md font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-obsidian md:text-5xl">
              Are patients finding you on Google — or your competitor?
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              Reach out and get a full audit report for your profile in <span className="font-semibold text-obsidian">24 hours</span> — exactly what&apos;s wrong, what needs fixing, and what could be way better. Here&apos;s what we&apos;ll put under the microscope:
            </p>
            <ul className="mt-6 max-w-md space-y-3">
              {[
                "Is your listing even verified — or is a bot ranking above you?",
                "Are your photos fresh, or stuck in 2019 and scaring patients off?",
                "Do you actually reply to reviews, or ghost the people paying you compliments?",
                "Posting weekly, or letting Google forget you exist?",
                "Hours, services & description complete — or a half-filled profile losing walk-ins?",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-lab-green" />
                  {line}
                </li>
              ))}
            </ul>

            <div className="mt-8 divide-y divide-slate-100 rounded-2xl border border-slate-200">
              {checks.map((c) => {
                const Icon = c.icon;
                return (
                  <label key={c.key} data-testid={`gmb-check-${c.key}`} className="flex items-center justify-between gap-4 p-4">
                    <span className="flex items-center gap-3 text-sm text-slate-700">
                      <Icon className={`h-4 w-4 ${state[c.key] ? "text-lab-green" : "text-slate-400"}`} />
                      {c.label}
                    </span>
                    <Switch
                      checked={state[c.key]}
                      onCheckedChange={(v) => setState((s) => ({ ...s, [c.key]: v }))}
                      data-testid={`gmb-switch-${c.key}`}
                    />
                  </label>
                );
              })}
            </div>
          </div>

          {/* Score card */}
          <div className="flex flex-col justify-center rounded-3xl border border-slate-200 bg-slate-50 p-8 md:p-12">
            <div className="flex flex-col items-center">
              <div className="relative h-40 w-40">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="12" />
                  <motion.circle
                    cx="60" cy="60" r="52" fill="none"
                    stroke={score >= 80 ? "#10B981" : score >= 50 ? "#0EA5E9" : "#F43F5E"}
                    strokeWidth="12" strokeLinecap="round"
                    strokeDasharray={circumference}
                    animate={{ strokeDashoffset: circumference - (score / 100) * circumference }}
                    transition={{ type: "spring", stiffness: 60, damping: 15 }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span data-testid="gmb-score" className="font-display text-5xl font-extrabold text-obsidian">{score}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">/ 100</span>
                </div>
              </div>
              <p className={`mt-5 font-display text-2xl font-bold ${verdict.c}`} data-testid="gmb-verdict">{verdict.t}</p>
              <p className="mt-2 max-w-xs text-center text-sm text-slate-500">
                {score >= 80
                  ? "Strong profile. Let's turn that visibility into booked appointments."
                  : "You're leaving patients on the table. We can fix this fast."}
              </p>
              <button
                data-testid="gmb-claim"
                onClick={claim}
                className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-obsidian px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-0.5"
              >
                <MessageSquare className="h-4 w-4" />
                Get my full GMB audit
              </button>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">Opens WhatsApp · report in 24h</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
